// =============================================================================
// MarkPaidModal — flip an unpaid booking to paid
// =============================================================================
// Used on BookingDetailScreen when payment_status='unpaid' (or pending /
// failed / none). Operator picks cash / card_terminal, confirms the amount,
// optional reason. Calls fn_admin_mark_booking_paid directly (RPC, no edge
// function — Stripe-paid bookings settle via the webhook).
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { formatError } from '../lib/errors';
import type { BookingRow } from '../types';

interface Props {
  booking: BookingRow;
  onClose: (changed: boolean) => void;
}

type Method = 'cash' | 'card_terminal';

const METHOD_LABEL: Record<Method, string> = {
  cash: 'Contant',
  card_terminal: 'Pinterminal',
};

export function MarkPaidModal({ booking, onClose }: Props) {
  const { operator } = useAuth();
  const [method, setMethod] = useState<Method>('cash');
  const [amountEur, setAmountEur] = useState(
    booking.total_cents != null ? (booking.total_cents / 100).toFixed(2) : '0.00',
  );
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  const amountCents = Math.round(Number(amountEur) * 100);
  const valid = Number.isFinite(amountCents) && amountCents > 0;

  const handleSubmit = useCallback(async () => {
    if (!valid || submitting || !operator?.id) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { error } = await supabase
        .schema('booking')
        .rpc('fn_admin_mark_booking_paid', {
          p_operator_id:    operator.id,
          // BookingRow.id is the DEPARTURE id post-EXPAND; the RPC keys on the
          // parent purchase (booking.bookings.id).
          p_booking_id:     booking.parent_booking_id,
          p_payment_method: method,
          p_amount_cents:   amountCents,
          p_reason:         reason.trim().length > 0 ? reason.trim() : null,
        });
      if (error) throw error;
      onClose(true);
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [valid, submitting, operator?.id, booking.id, method, amountCents, reason, onClose]);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* ✕ / Esc only */ }}>
      <div className="dep-modal cancel-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dep-modal-head">
          <div>
            <h2>Markeer als betaald</h2>
            <div className="dep-modal-sub muted">{booking.booking_number}</div>
          </div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => !submitting && onClose(false)}
            aria-label="Sluiten"
          >✕</button>
        </header>
        <div className="dep-modal-body">
          <div className="cancel-summary muted">
            Status: <strong>{booking.payment_status}</strong>
          </div>

          <div className="walkin-row">
            <label className="walkin-field">
              <span>Betaalmethode</span>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as Method)}
              >
                {(Object.keys(METHOD_LABEL) as Method[]).map((k) => (
                  <option key={k} value={k}>{METHOD_LABEL[k]}</option>
                ))}
              </select>
            </label>
            <label className="walkin-field">
              <span>Bedrag (€)</span>
              <input
                type="number"
                min={0.01}
                step="0.01"
                value={amountEur}
                onChange={(e) => setAmountEur(e.target.value)}
              />
            </label>
          </div>

          <label className="walkin-field cancel-reason">
            <span>Reden (optioneel, wordt gelogd)</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Bijv. klant betaalde bij aankomst"
            />
          </label>

          {submitError ? (
            <div className="error-card walkin-submit-error">{submitError}</div>
          ) : null}

          <div className="walkin-step-actions">
            <button
              className="btn-link"
              disabled={submitting}
              onClick={() => onClose(false)}
            >Annuleren</button>
            <button
              className="walkin-primary-btn"
              disabled={!valid || submitting}
              onClick={() => void handleSubmit()}
            >
              {submitting ? 'Bezig…' : 'Markeer als betaald'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
